/**
 * Sectorpilot Local Agent Client Service
 * Faz 2: Local POS Cache & Offline Order Queue Engine
 * 
 * Kasadaki Local Agent (Port 4005) ile haberleşir:
 * - Menü ve Masaları 0 ms ile yerel önbellekten çeker.
 * - Local Agent yoksa otomatik olarak Firestore Bulutuna fallback yapar.
 * - İnternet kesildiğinde siparişleri yerel kuyruğa alır ve bağlantı gelince buluta aktarır.
 */

import { getIndexedDBCache, setIndexedDBCache } from '../utils/localCacheHelper';

const DEFAULT_AGENT_PORT = 4005;
const PING_INTERVAL_MS = 15000;
const PING_TIMEOUT_MS = 2000;

class LocalAgentService {
  constructor() {
    this.status = 'unknown'; // 'online' | 'offline' | 'unknown'
    this.lastHealthData = null;
    this.listeners = new Set();
    this.isChecking = false;
    this.lastCheckTime = 0;
    
    // Auto-ping timer
    if (typeof window !== 'undefined') {
      this.initListeners();
    }
  }

  // Base URL (Localhost or configured cash register IP)
  getBaseUrl() {
    if (typeof window === 'undefined') return `http://localhost:${DEFAULT_AGENT_PORT}`;
    const customHost = localStorage.getItem('sectorpilot_cash_register_ip');
    if (customHost && customHost.trim()) {
      const cleanHost = customHost.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '');
      return `http://${cleanHost}:${DEFAULT_AGENT_PORT}`;
    }
    return `http://localhost:${DEFAULT_AGENT_PORT}`;
  }

  setCashRegisterIp(ip) {
    if (typeof window === 'undefined') return;
    if (ip && ip.trim()) {
      localStorage.setItem('sectorpilot_cash_register_ip', ip.trim());
    } else {
      localStorage.removeItem('sectorpilot_cash_register_ip');
    }
    this.checkHealth(true);
  }

  getCashRegisterIp() {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('sectorpilot_cash_register_ip') || '';
  }

  // Subscription for connection status badge
  subscribe(callback) {
    this.listeners.add(callback);
    callback({
      status: this.status,
      health: this.lastHealthData,
      baseUrl: this.getBaseUrl()
    });
    return () => this.listeners.delete(callback);
  }

  notify() {
    const payload = {
      status: this.status,
      health: this.lastHealthData,
      baseUrl: this.getBaseUrl()
    };
    this.listeners.forEach((cb) => {
      try { cb(payload); } catch (e) { console.error('LocalAgent listener error:', e); }
    });
  }

  initListeners() {
    // Check initial health
    this.checkHealth();

    // Auto-check periodically
    setInterval(() => {
      this.checkHealth();
    }, PING_INTERVAL_MS);

    // Browser online/offline event handlers
    window.addEventListener('online', () => {
      console.log('🌐 [Network] Browser is online, checking Local Agent and flushing queue...');
      this.checkHealth(true);
    });

    window.addEventListener('offline', () => {
      console.log('⚠️ [Network] Browser is offline, switching to Local/Offline mode.');
      this.checkHealth(true);
    });
  }

  // Health check with timeout
  async checkHealth(force = false) {
    const now = Date.now();
    if (!force && now - this.lastCheckTime < 5000) {
      return this.status === 'online';
    }
    if (this.isChecking) return this.status === 'online';

    this.isChecking = true;
    this.lastCheckTime = now;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

    try {
      const businessId = localStorage.getItem('activeBusinessId') || 'default';
      const url = `${this.getBaseUrl()}/local-api/health?businessId=${encodeURIComponent(businessId)}`;
      
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json();
        this.status = 'online';
        this.lastHealthData = data;
      } else {
        this.status = 'offline';
        this.lastHealthData = null;
      }
    } catch (err) {
      clearTimeout(timeoutId);
      this.status = 'offline';
      this.lastHealthData = null;
    } finally {
      this.isChecking = false;
      this.notify();
    }

    return this.status === 'online';
  }

  isLocalAgentAvailable() {
    return this.status === 'online';
  }

  // ==========================================
  // 📋 1. MENU CACHE
  // ==========================================

  async getMenu(businessId) {
    if (!businessId) return null;

    // 1. Try Local Agent if online
    if (this.status === 'online') {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1200);

        const res = await fetch(`${this.getBaseUrl()}/local-api/menu/${encodeURIComponent(businessId)}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (res.ok) {
          const json = await res.json();
          if (json.success && Array.isArray(json.items) && json.items.length > 0) {
            console.log(`⚡ [LocalAgent] Menu HIT from Local Cache (${json.items.length} items)`);
            return {
              items: json.items,
              categories: json.categories || [],
              fromCache: true,
              source: 'local_agent'
            };
          }
        }
      } catch (e) {
        // Fallback silently
      }
    }

    // 2. Try IndexedDB Cache
    try {
      const idbMenu = await getIndexedDBCache(`menu_${businessId}`);
      if (idbMenu && Array.isArray(idbMenu.items) && idbMenu.items.length > 0) {
        console.log(`💾 [IndexedDB] Menu HIT (${idbMenu.items.length} items)`);
        return {
          items: idbMenu.items,
          categories: idbMenu.categories || [],
          fromCache: true,
          source: 'indexed_db'
        };
      }
    } catch (e) {}

    return null; // Return null so caller falls back to Firebase
  }

  // Push latest menu to Local Agent & IndexedDB (Fire & Forget)
  async syncMenu(businessId, items, categories = []) {
    if (!businessId || !Array.isArray(items)) return;

    // Save to browser IndexedDB
    try {
      await setIndexedDBCache(`menu_${businessId}`, { items, categories, timestamp: Date.now() });
    } catch (e) {}

    // Send to Local Agent
    try {
      await fetch(`${this.getBaseUrl()}/local-api/menu/sync/${encodeURIComponent(businessId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, categories })
      });
    } catch (e) {}
  }

  // ==========================================
  // 🪑 2. TABLES CACHE
  // ==========================================

  async getTables(businessId) {
    if (!businessId) return null;

    if (this.status === 'online') {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1000);

        const res = await fetch(`${this.getBaseUrl()}/local-api/tables/${encodeURIComponent(businessId)}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (res.ok) {
          const json = await res.json();
          if (json.success && Array.isArray(json.tables)) {
            return json.tables;
          }
        }
      } catch (e) {}
    }

    return null;
  }

  async syncTables(businessId, tables) {
    if (!businessId || !Array.isArray(tables)) return;

    try {
      await fetch(`${this.getBaseUrl()}/local-api/tables/sync/${encodeURIComponent(businessId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tables })
      });
    } catch (e) {}
  }

  // ==========================================
  // 📥 3. OFFLINE ORDER QUEUE
  // ==========================================

  async queueOrder(order, businessId) {
    const bizId = businessId || order.businessId || localStorage.getItem('activeBusinessId') || 'default';
    const queueId = `off_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const queuedRecord = {
      ...order,
      _queueId: queueId,
      _queuedAt: Date.now(),
      _queuedTime: new Date().toISOString(),
      businessId: bizId
    };

    let queuedToLocalAgent = false;

    // 1. Try sending to Local Agent queue
    try {
      const res = await fetch(`${this.getBaseUrl()}/local-api/orders/queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: queuedRecord, businessId: bizId })
      });
      if (res.ok) {
        queuedToLocalAgent = true;
      }
    } catch (e) {}

    // 2. Double protection: Always store in browser IndexedDB
    try {
      const currentQueue = (await getIndexedDBCache(`offline_orders_${bizId}`)) || [];
      currentQueue.push(queuedRecord);
      await setIndexedDBCache(`offline_orders_${bizId}`, currentQueue);
    } catch (e) {
      console.warn('IndexedDB queue error:', e);
    }

    this.notify();
    return {
      success: true,
      queueId,
      queuedToLocalAgent,
      message: 'Sipariş çevrimdışı kuyruğa alındı. İnternet bağlantısı sağlandığında otomatik olarak buluta aktarılacaktır.'
    };
  }

  // Get count of all pending offline orders
  async getPendingCount(businessId) {
    const bizId = businessId || localStorage.getItem('activeBusinessId') || 'default';
    let count = 0;

    // From Local Agent
    if (this.lastHealthData?.offlineQueue?.pendingCount) {
      count = Math.max(count, this.lastHealthData.offlineQueue.pendingCount);
    }

    // From IndexedDB
    try {
      const idbQueue = (await getIndexedDBCache(`offline_orders_${bizId}`)) || [];
      count = Math.max(count, idbQueue.length);
    } catch (e) {}

    return count;
  }

  // Flush and sync all pending offline orders to Firestore
  async flushOfflineOrders(businessId, syncHandler) {
    if (typeof syncHandler !== 'function') return { success: false, error: 'No sync handler' };
    const bizId = businessId || localStorage.getItem('activeBusinessId') || 'default';

    console.log(`🔄 [LocalAgent] Flushing offline orders for ${bizId}...`);

    let ordersToSync = [];
    const syncedIds = [];

    // 1. Fetch from Local Agent
    try {
      const res = await fetch(`${this.getBaseUrl()}/local-api/orders/pending/${encodeURIComponent(bizId)}`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.orders)) {
          ordersToSync = json.orders;
        }
      }
    } catch (e) {}

    // 2. Fetch from IndexedDB if Local Agent empty or unreachable
    try {
      const idbQueue = (await getIndexedDBCache(`offline_orders_${bizId}`)) || [];
      if (idbQueue.length > 0) {
        // Merge without duplicates based on _queueId
        const existingIds = new Set(ordersToSync.map(o => o._queueId));
        idbQueue.forEach(o => {
          if (!existingIds.has(o._queueId)) {
            ordersToSync.push(o);
          }
        });
      }
    } catch (e) {}

    if (ordersToSync.length === 0) {
      return { success: true, count: 0 };
    }

    // 3. Process each order with syncHandler
    let successCount = 0;
    for (const order of ordersToSync) {
      try {
        await syncHandler(order);
        syncedIds.push(order._queueId);
        successCount++;
      } catch (err) {
        console.error(`❌ [LocalAgent] Failed to sync order ${order._queueId}:`, err);
        // Continue with others
      }
    }

    // 4. Acknowledge synced orders in Local Agent
    if (syncedIds.length > 0) {
      try {
        await fetch(`${this.getBaseUrl()}/local-api/orders/ack/${encodeURIComponent(bizId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ syncedQueueIds: syncedIds })
        });
      } catch (e) {}

      // Clean from IndexedDB
      try {
        const remainingIDB = (await getIndexedDBCache(`offline_orders_${bizId}`)) || [];
        const filtered = remainingIDB.filter(o => !syncedIds.includes(o._queueId));
        await setIndexedDBCache(`offline_orders_${bizId}`, filtered);
      } catch (e) {}
    }

    await this.checkHealth(true);
    return {
      success: true,
      total: ordersToSync.length,
      synced: successCount
    };
  }

  // ==========================================
  // 🛡️ 4. REAL-TIME DATA MIRROR, SYNC_META & MUTATION EVENT BUS
  // ==========================================

  getSupportedCollections() {
    return [
      { key: 'menu', label: 'Menü & Kategoriler', group: 'Operasyon' },
      { key: 'tables', label: 'Masalar & Bölgeler', group: 'Operasyon' },
      { key: 'orders', label: 'Perakende, Web & Platform Siparişleri', group: 'Siparişler' },
      { key: 'shipments', label: 'B2B / Toptan Sevkiyat Siparişleri', group: 'Siparişler' },
      { key: 'expenses', label: 'İşletme & Platform Giderleri', group: 'Finans & Kasa' },
      { key: 'cash_movements', label: 'Kasa Giriş/Çıkış Hareketleri', group: 'Finans & Kasa' },
      { key: 'daily_cash_status', label: 'Günlük Kasa Durumu', group: 'Finans & Kasa' },
      { key: 'z_reports', label: 'Gün Sonu (Z) Raporları', group: 'Finans & Kasa' },
      { key: 'payments', label: 'Tahsilat & Ödeme Kayıtları', group: 'Finans & Kasa' },
      { key: 'invoices', label: 'Faturalar', group: 'Finans & Kasa' },
      { key: 'customers', label: 'Müşteriler & Veresiye/Cari Hesaplar', group: 'Cari & Tedarik' },
      { key: 'suppliers', label: 'Tedarikçiler', group: 'Cari & Tedarik' },
      { key: 'supplier_payments', label: 'Tedarikçi Ödemeleri', group: 'Cari & Tedarik' },
      { key: 'staff', label: 'Personel Listesi', group: 'Personel & İK' },
      { key: 'staff_payments', label: 'Personel Maaş & Avans Ödemeleri', group: 'Personel & İK' },
      { key: 'staff_extra_work', label: 'Kurye Ekstra Paket & Mesai Kayıtları', group: 'Personel & İK' },
      { key: 'shifts', label: 'Vardiya Planları', group: 'Personel & İK' },
      { key: 'time_logs', label: 'Personel Giriş/Çıkış Puantajı', group: 'Personel & İK' },
      { key: 'special_cases', label: 'İzin, Rapor & Kesinti Kayıtları', group: 'Personel & İK' },
      { key: 'inventory_items', label: 'Stok & Hammadde Kalemleri', group: 'Stok & İmalat' },
      { key: 'stock_movements', label: 'Stok Hareketleri', group: 'Stok & İmalat' },
      { key: 'recipes', label: 'Ürün Reçeteleri', group: 'Stok & İmalat' },
      { key: 'production_plans', label: 'İmalat & Üretim Planları', group: 'Stok & İmalat' },
      { key: 'couriers', label: 'Kurye Tanımları', group: 'Operasyon' },
      { key: 'delivery_zones', label: 'Paket Servis Teslimat Bölgeleri', group: 'Operasyon' },
      { key: 'settings', label: 'İşletme & POS Ayarları', group: 'Ayarlar' }
    ];
  }

  /**
   * Herhangi bir modülde (Gider, Gelir, Kasa, Personel Ödemesi, Sipariş, B2B Sevkiyat, Cari, Stok)
   * kayıt eklendiğinde, güncellendiğinde veya silindiğinde çağrılır:
   * 1) Açık olan tüm ekranlara anında haber verir (Sayfa yenileme zorunluluğunu kaldırır).
   * 2) Cihazın IndexedDB önbelleğini günceller.
   * 3) Kasa bilgisayarı bağlıysa Local Agent'a AES-256 şifreli olarak anında yazar.
   * 4) Mobil ve uzak cihazlar için buluttaki tekil `settings/sync_meta` pusulasını günceller.
   */
  async recordMutation(businessId, collectionName, payload, action = 'upsert', options = {}) {
    const bizId = businessId || localStorage.getItem('activeBusinessId') || localStorage.getItem('currentBusinessId') || 'default';
    if (!bizId || !collectionName) return;

    const now = Date.now();
    const isBatch = action === 'batch_upsert' || action === 'replace' || Array.isArray(payload);

    // 1. Emit Global UI Event immediately (0ms Optimistic Reactivity across open tabs/modals)
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('sectorpilot:data-mutated', {
          detail: {
            businessId: bizId,
            collectionName,
            action,
            record: isBatch ? null : payload,
            records: isBatch ? payload : null,
            timestamp: now
          }
        }));
      } catch (e) {}
    }

    // 2. Update Browser IndexedDB Mirror (Works on both Mobile & Desktop)
    try {
      const idbKey = `mirror_${collectionName}_${bizId}`;
      const existingWrapper = (await getIndexedDBCache(idbKey)) || { items: [], cachedAt: 0 };
      let items = Array.isArray(existingWrapper.items) ? [...existingWrapper.items] : [];

      if (action === 'replace' && Array.isArray(payload)) {
        items = payload;
      } else if (action === 'batch_upsert' && Array.isArray(payload)) {
        const map = new Map();
        items.forEach(i => { if (i && i.id) map.set(String(i.id), i); });
        payload.forEach(r => {
          if (r && r.id) {
            const prev = map.get(String(r.id)) || {};
            map.set(String(r.id), { ...prev, ...r, _mirroredAt: now });
          }
        });
        items = Array.from(map.values());
      } else if (action === 'upsert' && payload && !Array.isArray(payload)) {
        const recId = String(payload.id || payload._id || `loc_${now}`);
        const normalized = { ...payload, id: recId, _mirroredAt: now };
        const idx = items.findIndex(i => i && String(i.id) === recId);
        if (idx > -1) items[idx] = { ...items[idx], ...normalized };
        else items.unshift(normalized);
      } else if (action === 'delete' && payload) {
        const targetId = String(typeof payload === 'string' ? payload : (payload.id || payload._id));
        items = items.filter(i => i && String(i.id) !== targetId);
      }

      await setIndexedDBCache(idbKey, {
        businessId: bizId,
        collectionName,
        cachedAt: now,
        count: items.length,
        items
      });

      // Update local manifest in IndexedDB
      const manifestKey = `sync_manifest_${bizId}`;
      const manifest = (await getIndexedDBCache(manifestKey)) || { collections: {} };
      if (!manifest.collections) manifest.collections = {};
      manifest.collections[collectionName] = {
        count: items.length,
        lastUpdatedAt: now
      };
      manifest.updatedAt = now;
      await setIndexedDBCache(manifestKey, manifest);
    } catch (e) {
      console.warn(`[LocalAgentService] IndexedDB mirror warning (${collectionName}):`, e.message);
    }

    // 3. Push to Local Agent (Port 4005 - AES-256 Encrypted Disk Vault)
    try {
      const bodyPayload = isBatch
        ? { action: action === 'replace' ? 'replace' : 'batch_upsert', records: payload }
        : { action, record: payload };

      fetch(`${this.getBaseUrl()}/local-api/mirror/${encodeURIComponent(bizId)}/${encodeURIComponent(collectionName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload)
      }).catch(() => {});
    } catch (e) {}

    // 4. Touch Cloud `settings/sync_meta` for Cross-Device / Mobile App Verification (Only on actual mutations, not initial read-mirrors)
    if (!options.skipCloudMeta && (action === 'upsert' || action === 'delete')) {
      this.touchCloudSyncMeta(bizId, collectionName, now).catch(() => {});
    }

    this.notify();
  }

  // Subscribe to data mutations in React components
  onDataMutated(callback) {
    if (typeof window === 'undefined') return () => {};
    const handler = (e) => {
      if (e && e.detail) callback(e.detail);
    };
    window.addEventListener('sectorpilot:data-mutated', handler);
    return () => window.removeEventListener('sectorpilot:data-mutated', handler);
  }

  // Lightweight 1-doc Cloud Sync Compass update (throttled per collection)
  async touchCloudSyncMeta(businessId, collectionName, timestamp = Date.now()) {
    if (!businessId || !collectionName) return;
    if (!this._metaThrottleMap) this._metaThrottleMap = new Map();
    const key = `${businessId}_${collectionName}`;
    const lastTouch = this._metaThrottleMap.get(key) || 0;
    if (timestamp - lastTouch < 2500) return; // Max once per 2.5s per collection
    this._metaThrottleMap.set(key, timestamp);

    try {
      const { db } = await import('./firebase');
      const { doc, setDoc } = await import('firebase/firestore');
      const metaRef = doc(db, 'businesses', 'food_beverage', 'businesses', businessId, 'settings', 'sync_meta');
      await setDoc(metaRef, {
        [`${collectionName}_updatedAt`]: timestamp,
        lastMutatedCollection: collectionName,
        updatedAt: timestamp,
        updatedIso: new Date(timestamp).toISOString()
      }, { merge: true });
    } catch (e) {}
  }

  // Read Cloud Sync Compass (1 Document Read) to verify if Mobile/Web local data is 100% up-to-date
  async getCloudSyncMeta(businessId) {
    if (!businessId) return null;
    try {
      const { db } = await import('./firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const metaRef = doc(db, 'businesses', 'food_beverage', 'businesses', businessId, 'settings', 'sync_meta');
      const snap = await getDoc(metaRef);
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      return null;
    }
  }

  // Get Comprehensive Backup & Sync Status (Merges Local Agent + IndexedDB + Cloud Sync Meta)
  async getBackupStatus(businessId) {
    const bizId = businessId || localStorage.getItem('activeBusinessId') || localStorage.getItem('currentBusinessId') || 'default';
    const supported = this.getSupportedCollections();

    // 1. Try Local Agent first
    let agentData = null;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      const res = await fetch(`${this.getBaseUrl()}/local-api/backup/status/${encodeURIComponent(bizId)}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          agentData = json;
          this.status = 'online';
        }
      }
    } catch (e) {}

    // 2. Read IndexedDB Manifest & Counts as fallback or supplement
    const idbManifest = (await getIndexedDBCache(`sync_manifest_${bizId}`)) || { collections: {} };
    const idbMenu = await getIndexedDBCache(`menu_${bizId}`);
    const idbPending = (await getIndexedDBCache(`offline_orders_${bizId}`)) || [];

    let totalRecords = 0;
    let latestSyncAt = agentData?.latestSyncAt || idbManifest?.updatedAt || 0;

    const collections = await Promise.all(supported.map(async (col) => {
      const agentCol = agentData?.collections?.find(c => c.key === col.key);
      let idbCount = idbManifest.collections?.[col.key]?.count || 0;
      let idbUpdatedAt = idbManifest.collections?.[col.key]?.lastUpdatedAt || null;

      if (col.key === 'menu' && idbCount === 0 && idbMenu?.items?.length) {
        idbCount = idbMenu.items.length;
        idbUpdatedAt = idbMenu.timestamp || null;
      }

      if (idbCount === 0 && !agentCol?.count) {
        try {
          const mirrorWrap = await getIndexedDBCache(`mirror_${col.key}_${bizId}`);
          if (mirrorWrap && Array.isArray(mirrorWrap.items)) {
            idbCount = mirrorWrap.items.length;
            idbUpdatedAt = mirrorWrap.cachedAt;
          }
        } catch (e) {}
      }

      const finalCount = Math.max(agentCol?.count || 0, idbCount || 0);
      const finalUpdatedAt = Math.max(agentCol?.lastUpdatedAt || 0, idbUpdatedAt || 0) || null;
      if (finalUpdatedAt && finalUpdatedAt > latestSyncAt) latestSyncAt = finalUpdatedAt;
      totalRecords += finalCount;

      return {
        key: col.key,
        label: col.label,
        group: col.group,
        count: finalCount,
        agentCount: agentCol?.count || 0,
        browserCount: idbCount || 0,
        sizeKB: agentCol?.sizeKB || (finalCount > 0 ? Math.max(0.5, (finalCount * 0.8)).toFixed(1) : '0.0'),
        lastUpdatedAt: finalUpdatedAt,
        encrypted: true
      };
    }));

    return {
      success: true,
      businessId: bizId,
      agentOnline: Boolean(agentData),
      agentVersion: agentData?.agentVersion || 'Web/Mobil IndexedDB',
      encryptionStandard: 'AES-256-GCM (KVKK Uyumlu Kriptolu Kasa)',
      dataDir: agentData?.dataDir || 'Tarayıcı / Mobil Yerel Kasa (IndexedDB)',
      pendingOfflineOrders: Math.max(agentData?.pendingOfflineOrders || 0, idbPending.length),
      totalRecords,
      totalSizeKB: agentData?.totalSizeKB || (totalRecords * 0.85).toFixed(1),
      cachedImagesCount: agentData?.cachedImagesCount || 0,
      latestSyncAt: latestSyncAt || null,
      collections
    };
  }

  // WebCrypto AES-256-GCM Encryption for Browser Fallback (.spbak)
  async encryptBrowserBundle(bundleObj, passphrase = '') {
    const secret = passphrase || `sectorpilot_aes256_vault_${bundleObj?.meta?.businessId || 'default'}`;
    if (typeof window !== 'undefined' && window.crypto?.subtle) {
      const enc = new TextEncoder();
      const salt = window.crypto.getRandomValues(new Uint8Array(16));
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const keyMaterial = await window.crypto.subtle.importKey('raw', enc.encode(secret), 'PBKDF2', false, ['deriveKey']);
      const key = await window.crypto.subtle.deriveKey(
        { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
      );
      const plainBytes = enc.encode(JSON.stringify(bundleObj));
      const cipherBuffer = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plainBytes);
      const toBase64 = (u8) => {
        let binary = '';
        for (let i = 0; i < u8.byteLength; i++) binary += String.fromCharCode(u8[i]);
        return window.btoa(binary);
      };
      return {
        _spEncrypted: true,
        alg: 'AES-256-GCM-WEBCRYPTO',
        salt: toBase64(salt),
        iv: toBase64(iv),
        payload: toBase64(new Uint8Array(cipherBuffer))
      };
    }
    return bundleObj;
  }

  async decryptBrowserBundle(wrapper, businessId, passphrase = '') {
    if (!wrapper || !wrapper._spEncrypted) return wrapper;
    if (wrapper.alg === 'AES-256-GCM-WEBCRYPTO' && typeof window !== 'undefined' && window.crypto?.subtle) {
      const secret = passphrase || `sectorpilot_aes256_vault_${businessId || 'default'}`;
      const enc = new TextEncoder();
      const dec = new TextDecoder();
      const fromBase64 = (b64) => {
        const bin = window.atob(b64);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        return u8;
      };
      const salt = fromBase64(wrapper.salt);
      const iv = fromBase64(wrapper.iv);
      const cipherBytes = fromBase64(wrapper.payload);
      const keyMaterial = await window.crypto.subtle.importKey('raw', enc.encode(secret), 'PBKDF2', false, ['deriveKey']);
      const key = await window.crypto.subtle.deriveKey(
        { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
      );
      const plainBuffer = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipherBytes);
      return JSON.parse(dec.decode(plainBuffer));
    }
    throw new Error('Bu şifreli yedek dosyası Local Agent (.exe) üzerinden çözülmelidir.');
  }

  // Export Full Backup (.spbak or JSON/CSV) with ZERO Firestore Reads
  async exportBackup(businessId, { format = 'spbak', passphrase = '' } = {}) {
    const bizId = businessId || localStorage.getItem('activeBusinessId') || localStorage.getItem('currentBusinessId') || 'default';

    // 1. If Local Agent is online, request from Local Agent Vault
    if (this.status === 'online') {
      try {
        const q = new URLSearchParams({ format, passphrase }).toString();
        const res = await fetch(`${this.getBaseUrl()}/local-api/backup/export/${encodeURIComponent(bizId)}?${q}`);
        if (res.ok) {
          const json = await res.json();
          if (json.success) return json;
        }
      } catch (e) {}
    }

    // 2. Fallback: Build bundle from Browser IndexedDB Mirror (0 Cloud Reads!)
    const supported = this.getSupportedCollections();
    const bundle = {
      meta: {
        generator: 'Sectorpilot Hybrid Local Mirror',
        version: '2.3.0',
        businessId: bizId,
        exportedAt: Date.now(),
        exportedIso: new Date().toISOString(),
        encryption: 'AES-256-GCM'
      },
      collections: {}
    };

    for (const col of supported) {
      let items = [];
      try {
        const mirrorWrap = await getIndexedDBCache(`mirror_${col.key}_${bizId}`);
        if (mirrorWrap && Array.isArray(mirrorWrap.items)) {
          items = mirrorWrap.items;
        } else if (col.key === 'menu') {
          const idbMenu = await getIndexedDBCache(`menu_${bizId}`);
          if (idbMenu && Array.isArray(idbMenu.items)) items = idbMenu.items;
        }
      } catch (e) {}
      bundle.collections[col.key] = items;
    }

    if (format === 'json') {
      return { success: true, bundle };
    }

    const encryptedArchive = await this.encryptBrowserBundle(bundle, passphrase);
    return {
      success: true,
      fileName: `sectorpilot_yedek_${bizId}_${new Date().toISOString().slice(0, 10)}.spbak`,
      archive: encryptedArchive
    };
  }

  // Restore Backup (.spbak) into Local Agent & IndexedDB
  async restoreBackup(businessId, archiveData, passphrase = '') {
    const bizId = businessId || localStorage.getItem('activeBusinessId') || localStorage.getItem('currentBusinessId') || 'default';

    // 1. Try Local Agent restore if online
    if (this.status === 'online') {
      try {
        const res = await fetch(`${this.getBaseUrl()}/local-api/backup/restore/${encodeURIComponent(bizId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ archive: archiveData, passphrase })
        });
        const json = await res.json();
        if (res.ok && json.success) {
          // Also populate IndexedDB with restored collections
          if (json.restoredData) {
            for (const [colKey, items] of Object.entries(json.restoredData)) {
              if (Array.isArray(items)) {
                await this.recordMutation(bizId, colKey, items, 'replace', { skipCloudMeta: true });
              }
            }
          }
          return json;
        } else if (!res.ok && archiveData.alg !== 'AES-256-GCM-WEBCRYPTO') {
          throw new Error(json.error || 'Geri yükleme başarısız');
        }
      } catch (e) {
        if (archiveData.alg !== 'AES-256-GCM-WEBCRYPTO') throw e;
      }
    }

    // 2. Browser WebCrypto Restore
    const decrypted = await this.decryptBrowserBundle(archiveData, bizId, passphrase);
    if (!decrypted || !decrypted.collections) {
      throw new Error('Geçersiz yedek dosyası formatı.');
    }

    let restoredCollections = 0;
    let restoredRecords = 0;
    for (const [colKey, items] of Object.entries(decrypted.collections)) {
      if (Array.isArray(items)) {
        await this.recordMutation(bizId, colKey, items, 'replace', { skipCloudMeta: true });
        restoredCollections++;
        restoredRecords += items.length;
      }
    }

    return {
      success: true,
      businessId: bizId,
      restoredCollections,
      restoredRecords,
      message: `${restoredCollections} veri tablosu (${restoredRecords} kayıt) başarıyla geri yüklendi.`
    };
  }
}

export const localAgentService = new LocalAgentService();
export default localAgentService;
